<template>
  <Dialog
    v-model:visible="visibleValue"
    modal
    :closable="false"
    :style="{ width: '40rem' }"
    aria-labelledby="confirm-delete-dialog-header"
    :pt="{
      header: { class: 'p-0' },
      content: { class: 'border-round pt-4' },
    }"
  >
    <Grid>
      <Col8 class="col-offset-2">
        <div class="flex justify-content-center">
          <div
            class="flex p-3 border-circle border-1 border-solid text-red-700 bg-red-100 border-red-700"
          >
            <span class="material-symbols-rounded">delete</span>
          </div>
        </div>
      </Col8>
      <Col2 class="flex align-items-start justify-content-end p-0">
        <IconButton @click="handleCancel" class="c-icon-button" :disabled="loading">
          <template #icon>
            <span class="material-symbols-rounded">close</span>
          </template>
        </IconButton>
      </Col2>

      <Col12>
        <h3 class="font-semibold text-center" id="confirm-delete-dialog-header">
          {{ resolvedHeader }}
        </h3>
      </Col12>
      <Col12>
        <span>{{ resolvedBody }}</span>
      </Col12>
      <Col12>
        <InputText
          v-model="confirmationText"
          id="deleteField"
          autocomplete="off"
          :placeholder="resolvedPlaceholder"
        />
      </Col12>
      <Col4 class="col-offset-2">
        <Button
          type="button"
          id="dialog-cancel-btn"
          data-test="dialog-cancel-btn"
          class="w-full"
          :class="`${ButtonSeverityClass.SECONDARY}`"
          :disabled="loading"
          :label="resolvedCancelLabel"
          @click="handleCancel"
        ></Button>
      </Col4>
      <Col4>
        <Button
          type="button"
          class="w-full"
          id="dialog-confirm-btn"
          data-test="dialog-confirm-btn"
          :class="`${ButtonSeverityClass.DANGER}`"
          :disabled="!isDeleteConfirmed || loading"
          :loading="loading"
          :label="resolvedConfirmLabel"
          @click="confirmDelete"
        ></Button>
      </Col4>
    </Grid>
  </Dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useTranslation } from '@centric-os/helpers';
import { Dialog } from '../../../core/overlays/dialog-modal';
import { Button, ButtonSeverityClass } from '../../../core/buttons';
import { InputText } from '../../../core/controls/input-text';
import { isEqual } from 'lodash-es';

const emit = defineEmits(['deleteConfirmed', 'cancel']);

const props = defineProps({
  recordName: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  header: {
    type: String,
    default: undefined,
  },
  body: {
    type: String,
    default: undefined,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  confirmLabel: {
    type: String,
    default: undefined,
  },
  cancelLabel: {
    type: String,
    default: undefined,
  },
});
const visibleValue = defineModel({ type: Boolean });

const confirmationText = ref<string>('');

// Set localization
const { translate } = useTranslation();

const resolvedHeader = computed<string>(
  () => props.header ?? translate(`confirmDeleteDialog.header`, { recordName: props.recordName }),
);

const resolvedBody = computed<string>(
  () => props.body ?? translate(`confirmDeleteDialog.body`, { type: props.type }),
);

const resolvedPlaceholder = computed<string | undefined>(() => {
  if (props.placeholder !== undefined) {
    return props.placeholder === '' ? undefined : props.placeholder;
  }

  return translate(`confirmDeleteDialog.placeholder`, { type: props.type });
});

const resolvedConfirmLabel = computed<string>(
  () => props.confirmLabel ?? translate(`confirmDeleteDialog.deleteLabel`),
);

const resolvedCancelLabel = computed<string>(
  () => props.cancelLabel ?? translate(`confirmDeleteDialog.cancelLabel`),
);

const isDeleteConfirmed = computed<boolean>(() =>
  isEqual(confirmationText.value, props.recordName),
);

const confirmDelete = (): void => {
  emit('deleteConfirmed');
};

const handleCancel = (): void => {
  visibleValue.value = false;
  emit('cancel');
};

watch(visibleValue, (newVal: boolean) => {
  if (!newVal) {
    confirmationText.value = '';
  }
});
</script>
